import { useState, useMemo, useRef } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FileUpload } from '@/components/ui/file-upload';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Image,
  Smartphone,
  Search,
  Check,
  ChevronsUpDown,
  Plus,
  X,
  Info,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { createWhiteLabel } from '@/api/white-labels';
import type { WizardFormData } from '../types';
import type { SchoolOptions } from '@/types/school';

interface BrandingStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  options: SchoolOptions | null;
  onRefreshOptions?: () => Promise<void>;
}

export function BrandingStep({ formData, onChange, options, onRefreshOptions }: BrandingStepProps) {
  const hasWhiteLabel = !!formData.whiteLabelEntityId;
  const isV2 = formData.deployV2?.enabled;

  // Combobox state
  const [comboOpen, setComboOpen] = useState(false);
  const [comboSearch, setComboSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newWhiteLabel, setNewWhiteLabel] = useState({ name: '', host: '', description: '', urlAndroid: '', urlIOS: '' });

  const selectedWhiteLabel = useMemo(() => {
    if (!formData.whiteLabelEntityId || !options?.whiteLabels) return null;
    return options.whiteLabels.find((wl) => wl.id === formData.whiteLabelEntityId) || null;
  }, [formData.whiteLabelEntityId, options?.whiteLabels]);

  const filteredWhiteLabels = useMemo(() => {
    if (!options?.whiteLabels) return [];
    if (!comboSearch.trim()) return options.whiteLabels;
    const s = comboSearch.toLowerCase();
    return options.whiteLabels.filter((wl) => wl.name.toLowerCase().includes(s));
  }, [options?.whiteLabels, comboSearch]);

  const handleSelect = (id: number | null) => {
    onChange({ whiteLabelEntityId: id });
    setComboOpen(false);
    setComboSearch('');
  };

  const handleCreateWhiteLabel = async () => {
    setCreateError(null);

    if (!newWhiteLabel.name.trim()) {
      setCreateError('Le nom est requis');
      return;
    }
    if (!newWhiteLabel.host.trim()) {
      setCreateError('Le domaine (host) est requis');
      return;
    }

    try {
      setCreating(true);
      const result = await createWhiteLabel({
        name: newWhiteLabel.name.trim(),
        host: newWhiteLabel.host.trim(),
        description: newWhiteLabel.description.trim() || undefined,
        urlAndroid: newWhiteLabel.urlAndroid.trim() || undefined,
        urlIOS: newWhiteLabel.urlIOS.trim() || undefined,
      });

      // Refresh options to include the new white label
      if (onRefreshOptions) {
        await onRefreshOptions();
      }

      // Select the newly created white label
      onChange({ whiteLabelEntityId: result.id });

      // Reset and close
      setNewWhiteLabel({ name: '', host: '', description: '', urlAndroid: '', urlIOS: '' });
      setCreateDialogOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la création';
      setCreateError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-foreground">Branding</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Personnalisez l'apparence de la plateforme
        </p>
      </div>

      <div className="grid gap-6">
        {/* Marque blanche */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Smartphone className="h-4 w-4 text-primary" />
              Marque blanche
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Configuration marque blanche</Label>

                {/* Combobox */}
                <PopoverPrimitive.Root open={comboOpen} onOpenChange={setComboOpen}>
                  <PopoverPrimitive.Trigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={comboOpen}
                      className={cn(
                        'flex min-h-12 w-full items-center justify-between',
                        'bg-muted/50 border-0 border-b-2 border-border rounded-t-lg rounded-b-none',
                        'px-4 pt-3 pb-2 text-sm',
                        'hover:bg-muted/70',
                        'focus:bg-muted/70 focus:border-b-primary focus:outline-none',
                      )}
                    >
                      <span className={selectedWhiteLabel ? '' : 'text-muted-foreground/60'}>
                        {selectedWhiteLabel ? selectedWhiteLabel.name : 'Aucune marque blanche'}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    </button>
                  </PopoverPrimitive.Trigger>

                  <PopoverPrimitive.Portal>
                    <PopoverPrimitive.Content
                      align="start"
                      sideOffset={4}
                      className={cn(
                        'z-50 w-[var(--radix-popover-trigger-width)] rounded-lg border bg-popover shadow-md',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out',
                        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                      )}
                      onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        searchInputRef.current?.focus();
                      }}
                    >
                      {/* Search */}
                      <div className="flex items-center border-b px-3">
                        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <input
                          ref={searchInputRef}
                          value={comboSearch}
                          onChange={(e) => setComboSearch(e.target.value)}
                          placeholder="Rechercher une marque blanche..."
                          className="flex h-10 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground/60"
                        />
                        {comboSearch && (
                          <X
                            className="h-4 w-4 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
                            onClick={() => setComboSearch('')}
                          />
                        )}
                      </div>

                      {/* Options */}
                      <div className="max-h-[300px] overflow-y-auto p-1">
                        {/* "Aucune" option */}
                        <button
                          type="button"
                          onClick={() => handleSelect(null)}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer',
                            'hover:bg-muted/50',
                            !formData.whiteLabelEntityId && 'bg-primary/5',
                          )}
                        >
                          <div
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                              !formData.whiteLabelEntityId
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'border-muted-foreground/30',
                            )}
                          >
                            {!formData.whiteLabelEntityId && <Check className="h-3 w-3" />}
                          </div>
                          <span className="text-muted-foreground">Aucune</span>
                        </button>

                        {filteredWhiteLabels.length === 0 && comboSearch.trim() ? (
                          <p className="py-4 text-center text-sm text-muted-foreground">
                            Aucun résultat
                          </p>
                        ) : (
                          filteredWhiteLabels.map((wl) => {
                            const isSelected = formData.whiteLabelEntityId === wl.id;
                            return (
                              <button
                                key={wl.id}
                                type="button"
                                onClick={() => handleSelect(wl.id)}
                                className={cn(
                                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer',
                                  'hover:bg-muted/50',
                                  isSelected && 'bg-primary/5',
                                )}
                              >
                                <div
                                  className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                    isSelected
                                      ? 'bg-primary border-primary text-primary-foreground'
                                      : 'border-muted-foreground/30',
                                  )}
                                >
                                  {isSelected && <Check className="h-3 w-3" />}
                                </div>
                                <span className="font-medium">{wl.name}</span>
                              </button>
                            );
                          })
                        )}
                      </div>

                      {/* Create button */}
                      <div className="border-t p-1">
                        <button
                          type="button"
                          onClick={() => {
                            setComboOpen(false);
                            setCreateDialogOpen(true);
                          }}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-muted/50 text-primary"
                        >
                          <Plus className="h-4 w-4" />
                          <span className="font-medium">Créer une nouvelle marque blanche</span>
                        </button>
                      </div>
                    </PopoverPrimitive.Content>
                  </PopoverPrimitive.Portal>
                </PopoverPrimitive.Root>
              </div>

              {/* V2 info box */}
              {hasWhiteLabel && isV2 && (
                <div className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Marque blanche en environnement V2</p>
                    <p>
                      En V2, la marque blanche crée un environnement complètement distinct
                      d'École Futée. Les comptes utilisateurs (parents, enseignants, directeurs)
                      sont directement associés à cette marque blanche et dissociés des comptes
                      École Futée. Un même utilisateur peut se connecter à École Futée d'un côté
                      et à la marque blanche de l'autre, avec des identifiants différents :
                      il s'agit de deux comptes distincts.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Images */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Image className="h-4 w-4 text-primary" />
              Images
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-6 ${hasWhiteLabel ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 max-w-xs'}`}>
              <div className="space-y-2">
                <Label>Logo principal</Label>
                <FileUpload
                  value={formData.logoFile}
                  onChange={(file) => onChange({ logoFile: file })}
                  label="Logo"
                  description="Affiché sur la plateforme"
                  maxSize={5 * 1024 * 1024}
                />
              </div>

              {hasWhiteLabel && (
                <>
                  <div className="space-y-2">
                    <Label>Logo connexion</Label>
                    <FileUpload
                      value={formData.logoSigninFile}
                      onChange={(file) => onChange({ logoSigninFile: file })}
                      label="Logo connexion"
                      description="Page de connexion"
                      maxSize={1 * 1024 * 1024}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Favicon</Label>
                    <FileUpload
                      value={formData.faviconFile}
                      onChange={(file) => onChange({ faviconFile: file })}
                      label="Favicon"
                      description="Icône navigateur"
                      maxSize={5 * 1024 * 1024}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Image de couverture</Label>
                    <FileUpload
                      value={formData.coverFile}
                      onChange={(file) => onChange({ coverFile: file })}
                      label="Couverture"
                      description="Page de connexion"
                      maxSize={5 * 1024 * 1024}
                    />
                  </div>
                </>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              {hasWhiteLabel
                ? "Les images peuvent être ajoutées après la création de l'école si nécessaire."
                : "Les images supplémentaires (logo connexion, favicon, couverture) sont disponibles lorsqu'une marque blanche est configurée."}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Create White Label Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle marque blanche</DialogTitle>
            <DialogDescription>
              Créer une marque blanche avec son tenant group V2.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {createError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {createError}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="wl-name">Nom *</Label>
              <Input
                id="wl-name"
                value={newWhiteLabel.name}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, name: e.target.value })}
                placeholder="Ex: Mon École Premium"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-host">Domaine (Host) *</Label>
              <Input
                id="wl-host"
                value={newWhiteLabel.host}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, host: e.target.value })}
                placeholder="Ex: mon-ecole.ecole-futee.com"
              />
              <p className="text-xs text-muted-foreground">
                Le domaine du tenant group V2 (sans https://)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-description">Description</Label>
              <Input
                id="wl-description"
                value={newWhiteLabel.description}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, description: e.target.value })}
                placeholder="Description optionnelle..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-urlAndroid">URL Application Android</Label>
              <Input
                id="wl-urlAndroid"
                type="url"
                value={newWhiteLabel.urlAndroid}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, urlAndroid: e.target.value })}
                placeholder="https://play.google.com/store/apps/..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-urlIOS">URL Application iOS</Label>
              <Input
                id="wl-urlIOS"
                type="url"
                value={newWhiteLabel.urlIOS}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, urlIOS: e.target.value })}
                placeholder="https://apps.apple.com/..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={creating}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleCreateWhiteLabel}
              disabled={creating}
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Création...
                </>
              ) : (
                'Créer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
